import { useGetManyUsers } from "#hooks/adapters/useUsers";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { UsersDropdownInput } from "#src/batteries-included-components/Dropdowns/UsersDropdownInput";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import {
  FilterArea,
  useFilterAreaContentContext,
  useFilterAreaContext,
} from "#src/components/FilterArea";
import { FilterDrawer } from "#src/components/FilterDrawer";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { TemplatedReportsTemplateDetailRoutePath } from "#src/routes/reports/templated-reports/template/[templateName]";
import { TemplatedReportsGeneratedReportDetailsRoutePath } from "#src/routes/reports/templated-reports/template/[templateName]/generated/[reportId]";
import { linkToUserDetailPage } from "#src/routes/settings/users/detail";
import {
  displayValueByDataType,
  displayValueWithUnknownDataType,
} from "#src/utils/display";
import { ExceptionUtils } from "#src/utils/exception";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Chip,
  DataTable,
  DataTablePanel,
  type DataTableProps,
  DateSelectorInput,
  HeaderType,
  type IconVariants,
  PillPopover,
  type PillPopoverOptionType,
  type PillPopoverType,
  PillToggleGroup,
  type PillToggleProps,
  PillToggleVariants,
  Tag,
  TextInput,
  useToast,
} from "@validereinc/common-components";
import {
  LookupWithSingleAttributeRefValueSchema,
  ReportStatusV3,
  type ReportStatusV3Type,
  ReportV3Adapter,
  ReportV3Schema,
  type ReportV3Type,
  type ReportVersionV3Type,
  Resources,
  SortDirection,
  type UserReturnType,
} from "@validereinc/domain";
import {
  downloadLink,
  getNameAbbreviation,
  invariant,
  toTitleCaseString,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import React, { useMemo } from "react";
import styles from "./TemplatedReportGeneratedReportsTablePanel.module.scss";

const cx = classNames.bind(styles);

export const TemplatedReportGeneratedReportsTablePanel = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [
    coreFilterConfigStorageKey,
    coreTableConfigStorageKey,
    coreTableStateStorageKey,
  ] = useStorageKey(
    ["filter-configuration", "table-configuration", "table-state"],
    "templated-reports-all-generated-reports"
  );
  const [quickFilterConfigStorageKey] = useStorageKey(
    ["filter-configuration"],
    "templated-reports-all-generated-reports-quick-filters"
  );
  const [coreFilters] = useStickyState<any>({}, coreFilterConfigStorageKey);
  const [quickFilters] = useStickyState<any>({}, quickFilterConfigStorageKey);
  const filters = useMemo(
    () => ({ ...coreFilters, ...quickFilters }),
    [coreFilters, quickFilters]
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "latest_version.created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    coreTableStateStorageKey
  );

  const getListQueryParams: Parameters<typeof ReportV3Adapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...(filters.search ? { name: { $like: filters.search } } : {}),
      ...(filters.status?.length && filters.status.every((s: string) => !!s)
        ? { status: { $in: filters.status } }
        : {}),
      ...(filters?.latest_version?.created_at
        ? {
            ["latest_version.created_at"]: {
              $and: [
                { $gte: filters?.latest_version?.created_at.from },
                { $lte: filters?.latest_version?.created_at.to },
              ],
            },
          }
        : {}),
      ...(filters?.latest_version?.created_by
        ? {
            ["latest_version.created_by"]: filters?.latest_version?.created_by,
          }
        : {}),
      // REVIEW: the endpoint needs the empty string explicitly to filter out
      // all reports except templated reports even when none are selected to
      // filter for. This is intentional here but perhaps the endpoint needs a
      // better design?
      templated_report_name: (!coreFilters.templated_report_name?.length
        ? { $like: "" }
        : { $in: filters.templated_report_name }) as any,
      isAlreadyFormatted: true,
    } as any,
  };
  const { data: allReports, isLoading: allReportsIsLoading } = useQuery({
    queryKey: [Resources.REPORT, getListQueryParams],
    queryFn: () => {
      return ReportV3Adapter.getList(getListQueryParams);
    },
    staleTime: 5 * 60 * 1000,
  });
  const allReportEntries = allReports?.data ?? [];

  const usersQuery = useGetManyUsers(
    Array.from(
      new Set(
        allReportEntries.flatMap(
          ({ created_by, updated_by, latest_version }) => [
            created_by ?? "",
            updated_by ?? "",
            latest_version.created_by ?? "",
          ]
        )
      )
    ).filter((item) => item)
  );
  const changeReportStatusMutation = useMutation({
    mutationFn: (params: Parameters<typeof ReportV3Adapter.updateOne>[0]) => {
      return ReportV3Adapter.updateOne(params);
    },
    onSuccess: () => {
      toast.push({
        intent: "success",
        description: "Successfully changed templated report status.",
      });
      queryClient.invalidateQueries({ queryKey: [Resources.REPORT] });
    },
    onError: (error) => {
      toast.push({
        intent: "error",
        description: "Failed to change templated report status.",
      });
      ExceptionUtils.reportException(error, "error", {
        sourceComponent: "TemplatedReportGeneratedReportsTablePanel",
      });
    },
  });
  const downloadReportMutation = useMutation({
    mutationFn: async ({
      report_id,
      version,
    }: Pick<ReportVersionV3Type, "report_id" | "version">) => {
      const { data: reportVersionData } = await ReportV3Adapter.versions.getOne(
        {
          id: report_id,
          meta: { version },
        }
      );

      invariant(
        reportVersionData.s3_download_link,
        "Report download link does not exist."
      );

      downloadLink(reportVersionData.s3_download_link, reportVersionData.name);
    },
    onError: (error) => {
      toast.push({
        intent: "error",
        description: "Failed to download report",
      });
      ExceptionUtils.reportException(error, "error", {
        sourceComponent: "TemplatedReportGeneratedReportsTablePanel",
      });
    },
  });

  const userMap = usersQuery.reduce(
    (accumulator: Record<string, UserReturnType>, current) => {
      if (current.data?.id) {
        accumulator[current.data.id] = current.data;
      }
      return accumulator;
    },
    {}
  );

  const headers: Array<HeaderType<ReportV3Type>> = [
    {
      label: "Name",
      key: "display_name",
      isSortable: true,
      renderComponent: ({ item }) =>
        item.id && item.templated_report?.name ? (
          <RoutingLink
            to={TemplatedReportsGeneratedReportDetailsRoutePath.toLinkParts({
              pathParams: {
                reportId: item.id,
                templateName: item.templated_report?.name,
              },
              queryParams: {
                version: item.latest_version.version.toString(),
              },
            })}
          >
            {item.name}
          </RoutingLink>
        ) : (
          item.name
        ),
    },
    // IMPROVE: show a report version or applicable year here in the future for more context and tie-in with the exportable reports tab where the report is actually exported
    {
      label: "Status",
      key: "status",
      isSortable: true,
      renderComponent: ({ item }) => {
        const reportStatusPillPopoverOptions: PillPopoverType<ReportStatusV3Type>["options"] =
          Object.entries(ReportStatusV3).map(([_, value]) => {
            return {
              label: toTitleCaseString(value, { intent: "ui" }),
              value,
              icon: (
                {
                  locked: "lock",
                  unlocked: null as any,
                  archived: null as any,
                } satisfies Record<ReportStatusV3Type, IconVariants | null>
              )[value],
              variant: (
                {
                  locked: "default",
                  unlocked: "info",
                  archived: "warning",
                } satisfies Record<
                  ReportStatusV3Type,
                  PillPopoverOptionType<ReportStatusV3Type>["variant"]
                >
              )[value],
            };
          });

        return (
          <PillPopover
            options={reportStatusPillPopoverOptions}
            value={item.status}
            isEditable
            isBusy={
              item.id === changeReportStatusMutation.variables?.id &&
              changeReportStatusMutation.isLoading
            }
            onChange={(newStatus: ReportStatusV3Type) =>
              changeReportStatusMutation.mutate({
                id: item.id,
                data: {
                  status: newStatus,
                },
              })
            }
          />
        );
      },
    },
    {
      label: "Template",
      key: "templated_report.display_name",
      isSortable: true,
      renderComponent: ({ item }) =>
        item.templated_report?.name ? (
          <RoutingLink
            to={TemplatedReportsTemplateDetailRoutePath.toLinkParts({
              pathParams: {
                templateName: item.templated_report?.name,
              },
            })}
          >
            {item.templated_report.display_name}
          </RoutingLink>
        ) : (
          item.templated_report_name
        ),
    },
    {
      label: "Inputs",
      key: "input",
      renderComponent: ({ item }) => {
        if (!isPlainObject(item.input)) return "unknown";

        const associatedInputSchema = item.templated_report?.input_schema;

        return (
          <ol className={cx("tag-container")}>
            {Object.entries(item.input).map(([k, v]) => {
              const displayLabel = associatedInputSchema
                ? (associatedInputSchema[k]?.display_name ??
                  toTitleCaseString(k, { intent: "ui" }))
                : toTitleCaseString(k, { intent: "ui" });
              const reportLookupInputValueParseResult =
                LookupWithSingleAttributeRefValueSchema.safeParse(v);

              return (
                <Tag
                  key={k}
                  as="li"
                  value={
                    associatedInputSchema?.[k]?.data_type
                      ? associatedInputSchema[k].data_type === "lookup" &&
                        associatedInputSchema[k].lookup_entity_attribute !==
                          undefined &&
                        associatedInputSchema[k]
                          .lookup_entity_attribute_data_type !== undefined
                        ? displayValueByDataType(
                            reportLookupInputValueParseResult.success
                              ? reportLookupInputValueParseResult.data.value
                              : v,
                            associatedInputSchema[k]
                              .lookup_entity_attribute_data_type
                          )
                        : displayValueByDataType(
                            v,
                            associatedInputSchema[k].data_type
                          )
                      : displayValueWithUnknownDataType(v)
                  }
                  label={displayLabel || "Unknown"}
                />
              );
            })}
          </ol>
        );
      },
    },
    {
      label: "Last Updated At",
      key: "latest_version.created_at",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.latest_version.created_at}
          withTime
        />
      ),
    },
    {
      label: "Last Updated By",
      key: "latest_version.created_by",
      isSortable: true,
      renderComponent: ({ item }) =>
        item.latest_version.created_by &&
        userMap[item.latest_version.created_by]?.name ? (
          <>
            <Chip
              text={
                getNameAbbreviation(
                  userMap[item.latest_version.created_by].name
                ) ?? ""
              }
              tooltipText={userMap[item.latest_version.created_by].name}
              style={{ marginRight: 4, minWidth: 26 }}
            />
            <RoutingLink
              to={linkToUserDetailPage(item.latest_version.created_by)}
            >
              {userMap[item.latest_version.created_by].name}
            </RoutingLink>
          </>
        ) : (
          "-"
        ),
    },
  ];

  const getItemActions: DataTableProps<ReportV3Type>["getItemActions"] = ({
    item,
  }) => [
    {
      label: "Export latest version",
      buttonProps: {
        onClick: () => {
          downloadReportMutation.mutate({
            report_id: item.id,
            version: item.latest_version.version,
          });
        },
        isLoading: downloadReportMutation.isLoading,
        icon: "arrow-square-out",
      },
    },
  ];

  const StatusPillToggleGroup = ({ name }: { name: string }) => {
    const { handleOnChange } = useFilterAreaContentContext();
    const { storedFilters: { status } = {} } = useFilterAreaContext<{
      status: string[];
    }>();

    const pillToggles: PillToggleProps[] = [
      {
        name: "All",
        label: "All",
        value: "",
        isSelected: !status?.length || status?.includes(""),
        shouldSelectAll: true,
      },
      {
        name: ReportStatusV3.UNLOCKED,
        label: toTitleCaseString(ReportStatusV3.UNLOCKED, { intent: "ui" }),
        value: ReportStatusV3.UNLOCKED,
        variant: PillToggleVariants.PENDING,
        isSelected: status?.includes(ReportStatusV3.UNLOCKED) ?? false,
      },
      {
        name: ReportStatusV3.LOCKED,
        label: toTitleCaseString(ReportStatusV3.LOCKED, { intent: "ui" }),
        value: ReportStatusV3.LOCKED,
        variant: PillToggleVariants.NEUTRAL,
        isSelected: status?.includes(ReportStatusV3.LOCKED) ?? false,
      },
      {
        name: ReportStatusV3.ARCHIVED,
        label: toTitleCaseString(ReportStatusV3.ARCHIVED, { intent: "ui" }),
        value: ReportStatusV3.ARCHIVED,
        variant: PillToggleVariants.ATTENTION,
        isSelected: status?.includes(ReportStatusV3.ARCHIVED) ?? false,
      },
    ];

    return (
      <PillToggleGroup
        name={name}
        pills={pillToggles}
        onChange={(val) => handleOnChange(val, name)}
      />
    );
  };

  const filterKeys = ReportV3Schema.keyof().Enum;

  return (
    <DataTablePanel
      storageKey={coreTableConfigStorageKey}
      panelProps={{
        title: "All Reports",
        titleDecorator: (
          <FilterArea.Root
            storageKey={quickFilterConfigStorageKey}
            defaultValues={{
              status: [ReportStatusV3.LOCKED, ReportStatusV3.UNLOCKED],
            }}
            applyDefaultValues
          >
            <FilterArea.Container aria-label="Status filter for generated reports">
              <FilterArea.Content>
                {() => <StatusPillToggleGroup name={filterKeys.status} />}
              </FilterArea.Content>
            </FilterArea.Container>
          </FilterArea.Root>
        ),
      }}
      filterComponent={
        <FilterArea.Root storageKey={coreFilterConfigStorageKey}>
          <FilterDrawer.Root>
            <FilterArea.Container aria-label="Filters for generated reports">
              <FilterArea.Content>
                {({ handleOnChange }) => (
                  <div style={{ marginRight: 8, marginBottom: 0 }}>
                    <TextInput
                      name="search"
                      type="search"
                      label="Search"
                      placeholder="Search by Name..."
                      isLabelShown={false}
                      isInline
                      onChange={(val) => handleOnChange(val, "search")}
                    />
                  </div>
                )}
              </FilterArea.Content>
              <FilterDrawer.Trigger />
              <FilterDrawer.Content>
                <DateSelectorInput
                  // REVIEW: can be accurately mapped to root property updated_at when bug is fixed https://validere.atlassian.net/browse/CHB-5596
                  name={`latest_version.${filterKeys.created_at}`}
                  label="Last Updated At"
                  placeholder="Select a date..."
                  isRange
                  variant="time"
                  isFluid
                  isInline={false}
                />
                <UsersDropdownInput
                  // REVIEW: can be accurately mapped to root property updated_by when bug is fixed https://validere.atlassian.net/browse/CHB-5596
                  name={`latest_version.${filterKeys.created_by}`}
                  label="Last Updated By"
                  placeholder="Select a User..."
                />
              </FilterDrawer.Content>
            </FilterArea.Container>
          </FilterDrawer.Root>
        </FilterArea.Root>
      }
      dataTableProps={{
        intent: "simplicity-first",
        isLoading:
          allReportsIsLoading || usersQuery.some(({ isLoading }) => isLoading),
        items: allReportEntries ?? [],
        headers,
        getItemId: (item) => item.id,
        getItemActions,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: allReports?.total_entries ?? 0,
        },
      }}
    />
  );
};
